import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Clock, Users, FileText, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { dashboardApi } from "@/api";
import { useLang, fmtMoney, fmtDate } from "@/i18n";
import { StatusBadge, TypeBadge } from "@/components/Badges";

const KpiCard = ({ icon: Icon, label, value, sub, testId, accent }) => (
  <div className="erp-card p-5 relative overflow-hidden group" data-testid={testId}>
    <div className={`absolute top-0 left-0 h-full w-1 ${accent}`} />
    <div className="flex items-start justify-between">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      <Icon size={18} className="text-slate-400 group-hover:text-blue-700 transition-colors" />
    </div>
    <div className="mt-3 text-2xl lg:text-3xl font-mono font-medium tracking-tight">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    dashboardApi.get().then(setData).catch(() => toast.error(t("error")));
  }, [t]);

  if (!data) return <div className="text-slate-500 text-sm" data-testid="dashboard-loading">…</div>;

  const monthLabel = (m) => new Date(`${m}-01`).toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", { month: "short" });

  return (
    <div className="space-y-6 page-enter" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">{t("dashboard_greeting")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("dashboard_hint")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 stagger">
        <KpiCard icon={TrendingUp} label={t("kpi_total_revenue")} value={fmtMoney(data.revenue)} accent="bg-emerald-500"
          sub={`${t("kpi_purchases")}: ${fmtMoney(data.purchases)}`} testId="kpi-total-revenue-card" />
        <KpiCard icon={Clock} label={t("kpi_unpaid_invoices")} value={fmtMoney(data.unpaid_total)} accent="bg-sky-500"
          sub={`${data.unpaid_count} · ${data.overdue_count} ${t("kpi_overdue")} (${fmtMoney(data.overdue_total)})`} testId="kpi-unpaid-invoices-card" />
        <KpiCard icon={Users} label={t("kpi_active_contacts")} value={data.contacts_total} accent="bg-blue-700"
          sub={`${data.customers} ${t("kpi_customers")} · ${data.suppliers} ${t("kpi_suppliers")}`} testId="kpi-contacts-card" />
        <KpiCard icon={FileText} label={t("kpi_documents")} value={data.documents_total} accent="bg-amber-500"
          sub={`${data.status_counts.draft} ${t("status_draft")} · ${data.status_counts.issued} ${t("status_issued")} · ${data.status_counts.paid} ${t("status_paid")} · ${t("kpi_payroll")}: ${fmtMoney(data.payroll_cost)}`}
          testId="kpi-documents-card" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="erp-card p-5 lg:col-span-2" data-testid="sales-chart-card">
          <h3 className="font-heading font-semibold mb-4">{t("chart_title")}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-800" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={70}
                  tickFormatter={(v) => `€${(v / 1000).toFixed(v >= 1000 ? 1 : 0)}${v >= 1000 ? "k" : ""}`} />
                <Tooltip formatter={(v) => fmtMoney(v)} labelFormatter={monthLabel}
                  contentStyle={{ borderRadius: 8, fontSize: 12, fontFamily: "JetBrains Mono" }} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sales" name={t("chart_sales")} fill="#1E40AF" radius={[3, 3, 0, 0]} />
                <Bar dataKey="purchases" name={t("chart_purchases")} fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-slate-800 p-5 bg-slate-900 text-slate-100 relative overflow-hidden grain" data-testid="vat-summary-card">
          <h3 className="font-heading font-semibold mb-4 relative">{t("vat_summary")}</h3>
          <div className="space-y-4 relative">
            <div className="flex justify-between items-baseline border-b border-slate-700 pb-3">
              <span className="text-sm text-slate-400">{t("vat_out")}</span>
              <span className="font-mono">{fmtMoney(data.vat_out)}</span>
            </div>
            <div className="flex justify-between items-baseline border-b border-slate-700 pb-3">
              <span className="text-sm text-slate-400">{t("vat_in")}</span>
              <span className="font-mono">− {fmtMoney(data.vat_in)}</span>
            </div>
            <div className="flex justify-between items-baseline pt-1">
              <span className="text-sm font-semibold">{t("vat_due")}</span>
              <span className="font-mono text-xl font-medium text-amber-300" data-testid="vat-due-value">{fmtMoney(data.vat_due)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="erp-card" data-testid="recent-documents-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <h3 className="font-heading font-semibold">{t("recent_docs")}</h3>
          <Link to="/documents" className="text-sm text-blue-700 dark:text-blue-300 inline-flex items-center gap-1 hover:underline" data-testid="view-all-documents-link">
            {t("view_all")} <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.recent.map((d) => (
            <Link key={d.id} to={`/documents/${d.id}`} data-testid={`recent-doc-${d.id}`}
              className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-sm font-medium">{d.number}</span>
                <TypeBadge type={d.type} className="hidden sm:inline-flex" />
                <span className="text-sm text-slate-600 dark:text-slate-300 truncate">{d.contact_name}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-slate-500 hidden sm:inline">{fmtDate(d.issue_date, lang)}</span>
                <StatusBadge doc={d} />
                <span className="font-mono text-sm w-28 text-right">{fmtMoney(d.total)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
